import * as React from 'react'
import { connect } from 'react-redux'
import { getFileId } from '../utils'
import { upploadFormFile } from '../../actions/forms'
import { RootState } from '../../types/rootstate'

export interface Files {
  [key: string]: SelectedFiles
}

export interface SelectedFile {
  error: null
  progress: number
  id: string
  localId
  file: File
  cancelToken: any
}

export interface SelectedFiles {
  selectedFiles: Array<SelectedFile>
  duplicated: Array<File>
  overLimit: Array<File>
}

export interface WithFilesState {
  files: Files
}

interface StateProps {

}

interface DispatchProps {
  upploadFormFile: (id: string,
    uniqLocalId: string,
    file: File,
    onProgress: (id: string, uniqLocalId: string, progress: number, uniqId?: string, cancel?) => void
  ) => Promise<string>
}

function mapStateToProps (state: RootState): StateProps {
  return {
  }
}

function mapDispatchToProps (dispatch): DispatchProps {
  return {
    upploadFormFile: (id,
      uniqLocalId,
      file,
      onProgress
    ) => dispatch(upploadFormFile(id, uniqLocalId, file, onProgress))
  }
}

export function withFilesState<T> (WrappedComponent) {
  type FilesStateProps = StateProps & DispatchProps

  return connect<StateProps, DispatchProps, T>(mapStateToProps, mapDispatchToProps)(
    class FilesState extends React.Component<FilesStateProps, WithFilesState> {

      state: WithFilesState = {
        files: {}
      }

      updateFileProgress = (
        id: string,
        uniqLocalId: string,
        progress: number,
        uniqId = '',
        cancelToken = null
      ) => {
        this.setState({
          files: {
            ...this.state.files,
            [id]: {
              ...this.state.files[id],
              selectedFiles: (this.state.files[id].selectedFiles || []).map(
                (data) => {
                  if (data.localId === uniqLocalId) {
                    return {
                      ...data,
                      id: uniqId,
                      progress,
                      cancelToken
                    }
                  }
                  return data
                }
              )
            }
          }
        })
      }

      addFiles = (id: string, files: Array<File>, limit = 1) => {
        const [ filesWithInLimit, overLimit ] = this._getWithInLimit(
          files,
          limit -
            (this.state.files[id] || { selectedFiles: [] }).selectedFiles.length
        )
        const [ toAdd, duplicated ] = this._removeDuplicates(
          id,
          filesWithInLimit
        )

        this.setState({
          files: {
            ...this.state.files,
            [id]: {
              ...this.state.files[id],
              selectedFiles: [
                ...((this.state.files[id] &&
                  this.state.files[id].selectedFiles) ||
                  []),
                ...toAdd.map((file) => ({
                  localId: getFileId(file),
                  progress: 0,
                  id: '',
                  file,
                  error: null,
                  cancelToken: ''
                }))
              ],
              duplicated,
              overLimit
            }
          }
        })

        return {
          added: toAdd,
          duplicated,
          overLimit
        }
      }

      setFileError = (id: string, localFileId: string, error) => {
        this.setState({
          files: {
            ...this.state.files,
            [id]: {
              ...this.state.files[id],

              selectedFiles: [
                ...((this.state.files[id] &&
                  this.state.files[id].selectedFiles) ||
                  [])
                  .map((selectedFile) => {
                    if (selectedFile.localId === localFileId) {
                      return {
                        ...selectedFile,
                        error
                      }
                    } else {
                      return selectedFile
                    }
                  })
              ]
            }
          }
        })
      }

      addAndSendFiles = (id: string, files: Array<File>, limit = 1) => {
        const {
          added,
          duplicated,
          overLimit
        } = this.addFiles(id, files, limit)

        const uploadPromises = added.map((file): Promise<string> => {
          const fileId = getFileId(file)

          // const promise = upploadFormFile(
          const promise = this.props.upploadFormFile(
            id,
            fileId,
            file,
            this.updateFileProgress)

          promise.catch((error) => {
            this.setFileError(id, fileId, error)
          })
          return promise
        })

        return {
          added,
          duplicated,
          overLimit,
          uploadPromises
        }
      }

      removeFile = (id: string, position: number) => {
        this.setState({
          files: {
            ...this.state.files,
            [id]: {
              ...this.state.files[id],
              selectedFiles: this.state.files[id].selectedFiles.filter(
                (data, _position) => {
                  if (_position !== position) {
                    return true
                  }

                  this._cancelRequest(data)
                  return false
                }
              )
            }
          }
        })
      }

      hasAtleastOneUploadedFile = (id: string) => {
        if (!(this.state.files[id] && this.state.files[id].selectedFiles.length >= 1 &&
          this.state.files[id].selectedFiles.find((selectedFile) => selectedFile.progress === 100))) {
          return false
        }

        return true
      }

      render () {
        return (
          <WrappedComponent
            {...this.props}
            addAndSendFiles={this.addAndSendFiles}
            files={this.state.files}
            addFiles={this.addFiles}
            removeFile={this.removeFile}
            updateFileProgress={this.updateFileProgress}
            hasAtleastOneUploadedFile={this.hasAtleastOneUploadedFile}
          />
        )
      }

      private _removeDuplicates (id: string, files: Array<File>) {
        const duplicated = []
        const toAdd = files.filter((file) => {
          const exists = (
            this.state.files[id] || {
              selectedFiles: [],
              overLimit: [],
              duplicated: []
            }
          ).selectedFiles.find(
            (selectedFile) => selectedFile.localId === getFileId(file)
          )

          if (exists) {
            duplicated.push(exists)
          }

          return !exists
        })

        return [ toAdd, duplicated ]
      }

      private _cancelRequest (data: SelectedFile) {
        if (data.cancelToken !== null) {
          try {
            data.cancelToken()
          } catch (error) {
            // swallow
          }
        }
      }

      private _getWithInLimit (
        files: Array<File>,
        limit: number
      ): Array<Array<File>> {
        const filesWithinLimit = []

        for (let index = 0; index < files.length; index++) {
          if (index >= limit) {
            break
          }

          const file = files[index]
          filesWithinLimit.push(file)
        }

        const filesOverLimit = files.slice(filesWithinLimit.length, files.length)

        return [ filesWithinLimit, filesOverLimit ]
      }
    })
}
